import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	TooltipHostCompat as TooltipHost,
	useTheme,
} from 'components/FluentUICompat';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
} from 'components/InfinityList';
import {CellWithEntityLink} from 'components/EntityList/CellWithEntityLink';
import {
	ArrayValues,
	DisassemblyStatusField,
	renderDateString,
	renderStatus,
	StatusField,
} from 'components/EntityList/ColumnRenderers';
import {getStatusTag} from 'components/StatusTag';
import {
	queryStringMongoArraySTD,
	queryStringMongoDateSTD,
	queryStringMongoEnumSTD,
} from 'components/InfinityList/InfinitListStandardHooks';
import {
	RegulatoryDocumentDisassemblyStatus,
	RegDocFilterOptions,
	WorkflowStatus,
} from 'types';

export const useAssignedRegDocColumns = () => {
	const {t: tReg} = useTranslation('features/regulations', {
		keyPrefix: 'RegulationsList',
	});
	const {t: tRegDoc} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'RegulatoryDocumentsList',
	});
	const {t: tWorkFlow} = useTranslation('common/enums', {
		keyPrefix: 'WorkflowStatus',
	});
	const theme = useTheme();

	const widthSTD120 = {minWidth: 120, maxWidth: 120};
	const widthSTD200 = {minWidth: 200, maxWidth: 300};
	const resizeMulti = {isResizable: true, isMultiline: true};
	const filterArraySimple = {filterOnFilter: true, filterType: 'Array'};
	const sortableDate120 = {
		...widthSTD120,
		sortable: true,
		filterType: 'Date',
		onRender: renderDateString(),
	};

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				key: 'regulationNumber',
				name: tReg('RegulationNumber'),
				fieldName: 'regulationNumber',
				...widthSTD200,
				...resizeMulti,
				...filterArraySimple,
				hiddenAlways: true,
			},
			{
				key: 'regulationVersion',
				name: tReg('Version'),
				fieldName: 'regulationVersion',
				...widthSTD200,
				...resizeMulti,
				onRender: (item: {
					id: string;
					regulation?: {regulationNumber: string; name: string} | null;
				}) => (
					<CellWithEntityLink href={`/regulatoryDocuments/${item.id}`}>
						<strong>{item.regulation?.regulationNumber ?? ''}</strong>
						{item.regulation?.name ? ` | ${item.regulation.name}` : ''}
					</CellWithEntityLink>
				),
			},
			{
				key: 'name',
				name: tRegDoc('Version'),
				fieldName: 'name',
				...widthSTD200,
				...resizeMulti,
			},
			{
				key: 'workflowStatus',
				name: tRegDoc('WorkflowStatus'),
				fieldName: 'workflowStatus',
				...widthSTD120,
				filterType: 'Array',
				onRender: (item: {workflow?: {status: any}}) =>
					getStatusTag(item.workflow?.status, theme, tWorkFlow),
			},
			{
				key: 'status',
				name: tRegDoc('Status'),
				fieldName: 'status',
				...widthSTD120,
				sortable: true,
				filterType: 'Array',
				onRender: renderStatus('RegulatoryDocumentStatus'),
			},
			{
				key: 'dateEffective',
				name: tRegDoc('DateEffective'),
				fieldName: 'dateEffective',
				...sortableDate120,
			},
		],
		[tReg, tRegDoc, tWorkFlow, theme],
	);

	return columns;
};

export const useAssignedRegDocColumnMetaData = () => {
	const columnMetaData: InfinityListColumnMetaData[] = [
		{
			key: 'regulationNumber',
			mongoDBFn: queryStringMongoArraySTD('regulationRef._id'),
		},
		{
			key: 'status',
			mongoDBFn: queryStringMongoEnumSTD('status'),
		},
		{
			key: 'workflowStatus',
			mongoDBFn: queryStringMongoEnumSTD('workflow.status'),
		},
		{
			key: 'dateEffective',
			mongoDBFn: queryStringMongoDateSTD('dateEffective'),
		},
	];
	return columnMetaData;
};

export const useAssignedRegDocFilterOptions = (
	regDocFilterOptions:
		| Pick<
				RegDocFilterOptions,
				'regulationOptions' | 'statusOptions' | 'workFlowOptions'
		  >
		| undefined,
) => {
	const columnFilterOptions: InfinityListColumnFilterOptions[] = useMemo(
		() => [
			{
				key: 'regulationNumber',
				fieldValuesUnique: (regDocFilterOptions?.regulationOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'status',
				fieldValuesUnique: (regDocFilterOptions?.statusOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'workflowStatus',
				fieldValuesUnique: (regDocFilterOptions?.workFlowOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
		],
		[regDocFilterOptions],
	);
	return columnFilterOptions;
};

export const useVehicleProjectRegDocTabColumns = (
	requirementCountMap: Record<string, number> = {},
	projectRelevancyMap: Record<string, boolean> = {},
) => {
	const {t: tReg} = useTranslation('features/regulations', {
		keyPrefix: 'RegulationsList',
	});
	const {t: tRegDoc} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'RegulatoryDocumentsList',
	});
	const {t: tEnum} = useTranslation('common/enums');
	const {t: tt} = useTranslation('features/regulations', {
		keyPrefix: 'TooltipsText',
	});
	const {t: tRelevant} = useTranslation('features/vehicleprojects', {
		keyPrefix: 'ProjectRelevantFields',
	});

	const widthSTD150 = {minWidth: 150, maxWidth: 200};
	const widthSTD200 = {minWidth: 200, maxWidth: 300};
	const resizeMulti = {isResizable: true, isMultiline: true};
	const filterArraySimple = {filterOnFilter: true, filterType: 'Array'};
	const filterArray150 = {...widthSTD150, ...resizeMulti, ...filterArraySimple};

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				key: 'regulationNumber',
				name: tReg('RegulationNumber'),
				fieldName: 'regulationNumber',
				...filterArray150,
				onRender: (item: {regulation?: {regulationNumber: string} | null}) =>
					item.regulation?.regulationNumber ?? '',
			},
			{
				key: 'regulationTitle',
				name: tReg('Name'),
				fieldName: 'regulationTitle',
				...widthSTD200,
				...resizeMulti,
				...filterArraySimple,
				onRender: (item: {regulation?: {name: string} | null}) =>
					item.regulation?.name ?? '',
			},
			{
				key: 'name',
				name: tRegDoc('Version'),
				fieldName: 'name',
				...widthSTD200,
				...resizeMulti,
				...filterArraySimple,
				onRender: (item: {id: string; name: string}) => (
					<CellWithEntityLink href={`/regulatoryDocuments/${item.id}`}>
						{item.name}
					</CellWithEntityLink>
				),
			},
			{
				key: 'projectRelevance',
				name: tRelevant('ColumnProjectRelevant'),
				fieldName: 'projectRelevance',
				minWidth: 120,
				maxWidth: 150,
				isResizable: true,
				onRender(item: {id: string}) {
					return projectRelevancyMap[item.id]
						? tRelevant('Yes')
						: tRelevant('No');
				},
			},
			{
				key: 'rd-status',
				name: tRegDoc('Status'),
				fieldName: 'status',
				minWidth: 100,
				maxWidth: 100,
				isResizable: true,
				onRender: renderStatus('RegulatoryDocumentStatus'),
			},
			{
				key: 'markets',
				name: tReg('Markets'),
				fieldName: 'markets',
				...filterArray150,
				onRender: (item: {markets: Array<{name: string}>}) => (
					<ArrayValues
						array={item.markets ?? []}
						getValue={m => m.name}
						limit={4}
					/>
				),
			},
			{
				key: 'mainKeywords',
				name: tReg('MainKeywords'),
				fieldName: 'mainKeywords',
				...filterArray150,
				onRender: (item: {
					regulation?: {mainKeywords: Array<{name: string}>} | null;
				}) => (
					<ArrayValues
						array={item.regulation?.mainKeywords ?? []}
						getValue={m => m.name}
						limit={4}
					/>
				),
			},
			{
				key: 'keywords',
				name: tReg('KeyWords'),
				fieldName: 'keywords',
				...filterArray150,
				onRender: (item: {
					regulation?: {keywords: Array<{name: string}>} | null;
				}) => (
					<ArrayValues
						array={item.regulation?.keywords ?? []}
						getValue={k => k.name}
						limit={4}
					/>
				),
			},
			{
				key: 'standardPlusPcmsClusters',
				name: tReg('StandardPlusPcmsClusters'),
				fieldName: 'standardPlusPcmsClusters',
				...filterArray150,
				tooltipHostProps: {
					content: tt('standardPlusPcmsClusters'),
				},
				onRender: (item: {
					regulation?: {standardPlusPcmsClusters: Array<{name: string}>} | null;
				}) => (
					<ArrayValues
						array={item.regulation?.standardPlusPcmsClusters ?? []}
						getValue={m => m.name}
						limit={4}
					/>
				),
			},
			{
				key: 'disassemblyStatus',
				name: tRegDoc('DisassemblyStatus'),
				fieldName: 'disassemblyStatus',
				...widthSTD150,
				isResizable: true,
				onRender: (
					item: {disassemblyStatus: RegulatoryDocumentDisassemblyStatus},
					index,
					column,
				) => (
					<DisassemblyStatusField
						item={item}
						i={index}
						col={column}
						value={item.disassemblyStatus}
						enumName='RegulatoryDocumentDisassemblyStatus'
					/>
				),
			},
			{
				key: 'requirementCount',
				name: tRegDoc('RequirementCount'),
				fieldName: 'requirementCount',
				minWidth: 120,
				maxWidth: 160,
				isResizable: true,
				onRender: (item: {id: string}) => requirementCountMap[item.id] ?? 0,
			},
		],
		[
			tReg,
			tRegDoc,
			tEnum,
			tt,
			tRelevant,
			filterArray150,
			filterArraySimple,
			widthSTD150,
			widthSTD200,
			resizeMulti,
			requirementCountMap,
			projectRelevancyMap,
		],
	);

	return columns;
};

export const useVehicleProjectRegDocTabColumnMetaData =
	(): InfinityListColumnMetaData[] => [
		{
			key: 'regulationNumber',
			mongoDBFn: queryStringMongoArraySTD('regulationRef._id'),
		},
		{
			key: 'regulationTitle',
			mongoDBFn: queryStringMongoArraySTD('regulationRef._id'),
		},
		{
			key: 'name',
			mongoDBFn: queryStringMongoArraySTD('name'),
		},
		{
			key: 'workflowStatus',
			mongoDBFn: queryStringMongoEnumSTD('workflow.status'),
		},
		{
			key: 'markets',
			mongoDBFn: queryStringMongoArraySTD('marketRefs._id'),
		},
		{
			key: 'mainKeywords',
			mongoDBFn: queryStringMongoArraySTD('regulationRef.mainKeywordRefs._id'),
		},
		{
			key: 'keywords',
			mongoDBFn: queryStringMongoArraySTD('keywordRefs._id'),
		},
		{
			key: 'standardPlusPcmsClusters',
			mongoDBFn: queryStringMongoArraySTD(
				'regulation.standardPlusPcmsClusterRefs._id',
			),
		},
	];

export const useVehicleProjectRegDocTabFilterOptions = (
	regDocFilterOptions:
		| Pick<
				RegDocFilterOptions,
				| 'regulationOptions'
				| 'workFlowOptions'
				| 'marketOptions'
				| 'mainKeywordOptions'
				| 'keywordOptions'
		  >
		| undefined,
) => {
	const columnFilterOptions: InfinityListColumnFilterOptions[] = useMemo(
		() => [
			{
				key: 'regulationNumber',
				fieldValuesUnique: (regDocFilterOptions?.regulationOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'regulationTitle',
				fieldValuesUnique: (regDocFilterOptions?.regulationOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'name',
				fieldValuesUnique: [],
			},
			{
				key: 'workflowStatus',
				fieldValuesUnique: (regDocFilterOptions?.workFlowOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'markets',
				fieldValuesUnique: (regDocFilterOptions?.marketOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'mainKeywords',
				fieldValuesUnique: (regDocFilterOptions?.mainKeywordOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'keywords',
				fieldValuesUnique: (regDocFilterOptions?.keywordOptions ?? []).map(
					x => ({optionKey: x.optionKey, name: x.name}),
				),
			},
			{
				key: 'standardPlusPcmsClusters',
				fieldValuesUnique: [],
			},
		],
		[regDocFilterOptions],
	);
	return columnFilterOptions;
};
